import { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { AdminActionButton } from '../../../../Component/UI/AdminPageHeader';
import { handleChatComposerKeyDown } from '../../../../Component/Support/chatComposerKeyboard';
import ChatComponent from '../../../Chat/ChatComponent';
import { useLiveQa } from '../../../Chat/liveQa';
import { live_info_type } from '../../../User/Live/Live';
import LiveQaReply from './LiveQaReply';
import LiveSideActions from './LiveSideActions';

export default function LiveChatContent({ liveInfo, liveSeq, onPurchaseNotification, moderationFilters = false, showNicknameSeparator = true }: {
  liveInfo?: live_info_type;
  liveSeq: number;
  onPurchaseNotification: (content: string) => void;
  moderationFilters?: boolean;
  showNicknameSeparator?: boolean;
}) {
  const [message, setMessage] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchIndex, setSearchIndex] = useState(0);
  const [searchCount, setSearchCount] = useState(0);
  const [hideForbidden, setHideForbidden] = useState(false);
  const [hideMuted, setHideMuted] = useState(false);
  const chatRef = useRef<{ sendMessage: () => Promise<boolean> }>(null);
  const streaming = ['방송중', '리허설'].includes(liveInfo?.live_status || '');
  const readable = streaming || liveInfo?.live_status === '종료';
  const { items: replies } = useLiveQa(liveSeq, 'manage', streaming);
  const answeredMessageIds = useMemo(() => new Set(replies.filter(reply => reply.answer.trim()).map(reply => reply.messageId)), [replies]);
  useEffect(() => { setSearchInput(''); setSearchQuery(''); setSearchIndex(0); setSearchCount(0); }, [liveSeq, streaming]);
  useEffect(() => { setHideForbidden(false); setHideMuted(false); }, [liveSeq]);
  useEffect(() => { setSearchIndex(index => Math.min(index, Math.max(0, searchCount - 1))); }, [searchCount]);
  const moveSearch = (direction: number) => {
    if (searchCount) setSearchIndex(index => (index + direction + searchCount) % searchCount);
  };
  const clearSearch = () => { setSearchInput(''); setSearchQuery(''); setSearchIndex(0); };
  const sendMessage = async () => {
    if (!chatRef.current || !streaming || !message.trim()) return;
    const content = message;
    if (await chatRef.current.sendMessage()) setMessage(current => current === content ? '' : current);
  };

  return <StandaloneChat>
    <SearchArea>
      {moderationFilters && <FilterRow role="group" aria-label="채팅 숨김 설정">
        <FilterToggle type="button" aria-label="채팅 금지 채팅 표시" aria-pressed={!hideMuted}
          title={hideMuted ? '채팅 금지 메시지 숨김 · 누르면 표시' : '채팅 금지 메시지 표시 중 · 누르면 숨김'} onClick={() => setHideMuted(value => !value)}>
          <VisibilityIcon hidden={hideMuted} />채팅 금지
        </FilterToggle>
        <FilterToggle type="button" aria-label="금지어 채팅 표시" aria-pressed={!hideForbidden}
          title={hideForbidden ? '금지어 메시지 숨김 · 누르면 표시' : '금지어 메시지 표시 중 · 누르면 숨김'} onClick={() => setHideForbidden(value => !value)}>
          <VisibilityIcon hidden={hideForbidden} />금지어
        </FilterToggle>
      </FilterRow>}
      <SearchForm data-admin-search="" role="search" aria-label="라이브 채팅 검색" onSubmit={event => {
        event.preventDefault(); const query = searchInput.trim();
        if (query && query === searchQuery) moveSearch(1);
        else { setSearchQuery(query); setSearchIndex(0); }
      }}>
        <input aria-label="채팅 검색어" type="search" placeholder="닉네임 · 채팅 내용 검색" value={searchInput} disabled={!readable}
          onChange={event => setSearchInput(event.target.value)} onKeyDown={event => {
            if (event.nativeEvent.isComposing) { if (event.key === 'Enter') event.preventDefault(); return; }
            if (event.key === 'Escape') clearSearch();
            if (event.key === 'ArrowUp' || event.key === 'ArrowDown') { event.preventDefault(); moveSearch(event.key === 'ArrowUp' ? -1 : 1); }
          }} />
        <AdminActionButton type="submit" disabled={!readable || !searchInput.trim()}>검색</AdminActionButton>
      </SearchForm>
      {searchQuery && <SearchNavigation>
        <span role="status">{searchCount ? `${searchIndex + 1} / ${searchCount}건` : '검색 결과가 없습니다.'}</span>
        <AdminActionButton type="button" aria-label="이전 검색 결과" disabled={!searchCount} onClick={() => moveSearch(-1)}>
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 15 6-6 6 6" /></svg>
        </AdminActionButton>
        <AdminActionButton type="button" aria-label="다음 검색 결과" disabled={!searchCount} onClick={() => moveSearch(1)}>
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
        </AdminActionButton>
        <AdminActionButton type="button" onClick={clearSearch}>닫기</AdminActionButton>
      </SearchNavigation>}
    </SearchArea>
    {readable ? <ChatComponent ref={chatRef} mesage={message} all_visible live_info={liveInfo!} readOnly={!streaming}
      searchQuery={searchQuery} searchIndex={searchIndex} onSearchMatches={setSearchCount}
      showNicknameSeparator={showNicknameSeparator}
      hideForbiddenMessages={moderationFilters && hideForbidden} hideMutedMessages={moderationFilters && hideMuted}
      adminView answeredMessageIds={answeredMessageIds} renderReply={(question, onClose) => <LiveQaReply liveSeq={liveSeq} question={question} onClose={onClose} />}
      renderUserActions={user => <LiveSideActions key={user.userId} nickname={user.nickname} restriction={user.restriction} compact />}
      onPurchaseNotification={onPurchaseNotification} />
      : <ChatEmpty role="status">{liveInfo?.live_status === '종료' ? '종료된 방송입니다.' : '방송이 시작되면 실시간 채팅이 표시됩니다.'}</ChatEmpty>}
    <ChatForm onSubmit={event => { event.preventDefault(); void sendMessage(); }}>
      <textarea aria-label="라이브 채팅 메시지" value={message} rows={Math.min(3, message.split('\n').length)} maxLength={100} enterKeyHint="send" disabled={!streaming} placeholder={readable && !streaming ? '종료된 방송은 이전 채팅만 볼 수 있습니다.' : '메시지를 입력해주세요.'}
        onChange={event => setMessage(event.target.value)}
        onKeyDown={event => handleChatComposerKeyDown(event, setMessage, () => { void sendMessage(); })} />
      <button type="submit" disabled={!streaming || !message.trim()} title={!streaming ? '리허설·방송 중에 채팅을 전송할 수 있습니다.' : undefined}>전송</button>
    </ChatForm>
  </StandaloneChat>;
}

function VisibilityIcon({ hidden }: { hidden: boolean }) {
  return <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z" />
    <circle cx="12" cy="12" r="3" />
    {hidden && <path d="m3 3 18 18" />}
  </svg>;
}

const StandaloneChat = styled.div`
  display: flex; flex-direction: column; height: 100%; min-height: 0; gap: var(--space-3);
`;
const SearchArea = styled.div`
  display: flex; flex-wrap: wrap; align-items: center; gap: var(--space-2); flex-shrink: 0; min-width: 0;
`;
const FilterRow = styled.div`
  display: flex; flex: 0 0 auto; align-items: center; gap: var(--space-2);
`;
const FilterToggle = styled(AdminActionButton)`
  && { min-height: var(--control-sm); padding: 0 var(--space-2); font-weight: 500; color: var(--color-text-secondary); }
  &&[aria-pressed="true"] { background: var(--color-bg-inverse); border-color: var(--color-bg-inverse); color: var(--color-text-on-inverse); }
  &&[aria-pressed="true"]:hover { background: var(--color-bg-inverse-hover); border-color: var(--color-bg-inverse-hover); }
  svg { width: var(--space-5); height: var(--space-5); flex-shrink: 0; fill: none; stroke: currentColor; stroke-width: 1.75; stroke-linecap: round; stroke-linejoin: round; }
  @media (pointer: coarse) { && { min-width: var(--touch-target); min-height: var(--touch-target); } }
`;
const SearchForm = styled.form`
  display: flex; flex: 1 1 180px; min-width: 0; gap: var(--space-2); align-items: center;
  input { min-width: 0; flex: 1; height: var(--control-sm); box-sizing: border-box; padding: 0 var(--space-2);
    border: 1px solid var(--color-border-default); border-radius: var(--radius-md); background: var(--color-bg-surface);
    color: var(--color-text-primary); font: inherit; font-size: var(--text-body); }
  input:focus-visible { outline: 2px solid var(--color-focus-ring); outline-offset: 2px; }
  && ${AdminActionButton} { min-height: var(--control-sm); height: var(--control-sm); padding: 0 var(--space-2); font-size: var(--text-caption); }
  @media (pointer: coarse) {
    input { min-height: var(--touch-target); height: var(--touch-target); }
    && ${AdminActionButton} { min-height: var(--touch-target); height: var(--touch-target); }
  }
`;
const SearchNavigation = styled.div`
  display: flex; flex: 1 0 100%; align-items: center; gap: var(--space-2);
  span { flex: 1; color: var(--color-text-secondary); font-size: var(--text-caption); }
  svg { width: var(--space-4); height: var(--space-4); fill: none; stroke: currentColor; stroke-width: 2; }
  && ${AdminActionButton} { min-height: var(--control-sm); height: var(--control-sm); padding: 0 var(--space-2); font-size: var(--text-caption); }
  @media (pointer: coarse) { && ${AdminActionButton} { min-width: var(--touch-target); min-height: var(--touch-target); height: var(--touch-target); } }
`;
const ChatEmpty = styled.p`
  flex: 1; margin: 0; color: var(--color-text-secondary); font-size: var(--text-body-lg); line-height: 1.6;
`;
const ChatForm = styled.form`
  display: flex; align-items: center; gap: var(--space-2); flex-shrink: 0; font-size: var(--text-body-lg);
  textarea, button { box-sizing: border-box; min-height: var(--control-sm); border-radius: var(--radius-md); font: inherit; }
  textarea { min-width: 0; flex: 1; resize: none; padding: var(--space-1) var(--space-2); line-height: var(--leading-body); border: 1px solid var(--color-border-default);
    background: var(--color-bg-surface); color: var(--color-text-primary); }
  button { padding: 0 var(--space-2); border: 0; background: var(--color-bg-inverse); color: var(--color-text-on-inverse); cursor: pointer; }
  && button { font-size: var(--text-caption); }
  button:disabled { background: var(--color-bg-muted); color: var(--color-text-disabled); cursor: not-allowed; }
  textarea:focus-visible, button:focus-visible { outline: 2px solid var(--color-focus-ring); outline-offset: 2px; }
  @media (pointer: coarse) { textarea, button { min-height: var(--touch-target); } }
`;
